import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HeroImage } from '@/hooks/useSiteContent';
import { mediaUrl } from '@/lib/api';

/**
 * Renders the hero image(s) behind the headline.
 *  - 0 images → nothing (the CSS `--ek-img-hero` fallback keeps working).
 *  - 1 image → single static layer.
 *  - 2+ images → crossfading slideshow (800ms fade, respects prefers-reduced-motion).
 *
 * Positioned absolutely so it sits under `.ek-top` content; the existing
 * `.ek-top::before` gradient still provides the bottom-fade.
 */
export default function HeroSlideshow({
  images,
  intervalMs,
}: {
  images: HeroImage[];
  intervalMs?: number;
}) {
  const sources = useMemo(
    () =>
      images
        .map((img) => mediaUrl(img.url || `/api/files/${img.file_id}`))
        .filter(Boolean),
    [images],
  );
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  activeRef.current = active;

  // Preload each image once so the crossfade never shows a flash of blank.
  useEffect(() => {
    sources.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [sources]);

  useEffect(() => {
    if (sources.length < 2) return;
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const step = Math.max(2000, Math.min(30000, intervalMs ?? 6000));
    const t = window.setInterval(() => {
      activeRef.current = (activeRef.current + 1) % sources.length;
      setActive(activeRef.current);
    }, step);
    return () => window.clearInterval(t);
  }, [sources.length, intervalMs]);

  if (sources.length === 0) return null;

  return (
    <div
      className="ek-hero-slides"
      aria-hidden="true"
      data-testid="hero-slideshow"
      data-count={sources.length}
    >
      {sources.map((src, i) => (
        <span
          key={src + i}
          className={`ek-hero-slide ${i === active ? 'is-on' : ''}`}
          style={{ backgroundImage: `url(${src})` }}
        />
      ))}
    </div>
  );
}
